import axios, { AxiosHeaders, type AxiosAdapter, type AxiosError, type AxiosInstance, type InternalAxiosRequestConfig } from 'axios';
import toast from 'react-hot-toast';
import { validateTenantScope, type AuthPolicy, type TenantScope } from './identity';
import {
  isOperationRequirementCurrent,
  markOperationRequirementTransportEntered,
  operationRequirementSignal,
  SessionTransitionError,
  type OperationRequirement,
} from './operation';
import { getCommittedOrganization, getOrganizationGeneration, getOrganizationSignal, hasCommittedOrganizationIdentity } from './organization';
import { emitSessionTelemetry } from './telemetry';
import { getAccessToken, getAuthGeneration, getSessionAuthSignal, getSessionSubject, refreshAccessToken, requestReauthentication } from './token';
import { isAdminTargetCapability, type AdminTargetCapability } from './adminTargetCapability';

export const ORGANIZATION_HEADER = 'X-Astur-Organization';
export const AUTHORIZATION_HEADER = 'Authorization';

declare module 'axios' {
  interface AxiosRequestConfig {
    // Explicit tenant scope for application callers (set by the scoped
    // transports). The missing-scope fallback preserves legacy raw-client
    // behavior but is not sanctioned for new application code.
    organizationScope?: TenantScope;
    // Session authentication is the default for the Astur Axios clients.
    // Public endpoints opt out explicitly; a caller-preset Authorization
    // header is not an implicit opt-out from actor liveness.
    authPolicy?: AuthPolicy;
    // A time-spanning workflow threads the same requirement through each
    // request so durable-dispatch downgrade happens inside the transport.
    operationRequirement?: OperationRequirement;
    // GET endpoints that mutate server state opt out of read cancellation.
    semanticWrite?: boolean;
    // Internal marker used only by the audited read-like POST wrappers. It is
    // intentionally absent from endpoint request-option types.
    _sessionSemanticRead?: true;
    // Private capability carried only by audited administrator-target wrappers.
    _sessionAdminTargetCapability?: AdminTargetCapability;
  }
}

type SessionInternalConfig = InternalAxiosRequestConfig & {
  _sessionAuthInjected?: boolean;
  _sessionDispatchAuthGeneration?: number;
  _sessionDispatchSubject?: string | null;
  _sessionDispatchAuthSignal?: AbortSignal;
  _sessionCallerSignal?: AbortSignal;
  _sessionReadSignal?: AbortSignal;
  _sessionOriginOrganization?: string;
  _sessionOriginOrganizationGeneration?: number;
  _sessionOriginOrganizationSignal?: AbortSignal;
  _sessionOriginOrganizationWasCommitted?: boolean;
  _sessionOperationDispatched?: boolean;
  _sessionOperationAdapterWrapped?: boolean;
  _sessionEtagKey?: string;
  _sessionRetry?: boolean;
  _sessionTransport?: 'api' | 'channelApi';
};

export { isSessionTransition, SessionTransitionError } from './operation';

// Registration is permanent by design: api and channelApi register exactly
// once per process at module import, and clearSessionEtags must keep reaching
// their stores for identity-transition and logout hygiene. Do NOT clear this
// Set from a test reset - dropping a singleton's store would silently disable
// ETag clearing for every later test in the worker. Test-created clients add
// one small Map each; clearSessionEtags empties them and the growth is
// bounded by the number of configure calls, so it needs no reset.
const etagStores = new Set<Map<string, string>>();
let failedRefreshCount = 0;
let lastFailedRefreshTime = 0;

const recordRefreshFailure = (): void => {
  const now = Date.now();
  if (now - lastFailedRefreshTime > 30_000) failedRefreshCount = 0;
  failedRefreshCount += 1;
  lastFailedRefreshTime = now;

  // Stable id: N parallel requests failing against the same expired refresh
  // token must collapse to one toast, not stack N of them.
  toast.error(
    failedRefreshCount >= 3
      ? 'Authentication issues detected. Some features may not work properly.'
      : 'Authentication error occurred. Some features may not work properly.',
    { id: 'session-auth-refresh-failure' }
  );
};

const isAuthenticationError = (error: AxiosError): boolean =>
  error.response?.status === 401 ||
  (error.response?.status === 400 &&
    (error.response.data as { errors?: { auth?: string } } | undefined)?.errors?.auth === 'Error validating token, please try again');

// Actor currency for the dispatched request (lifecycle policy P4): the
// subject and auth generation captured at dispatch must still be live before
// a 401 replay is attempted and before a response publishes to callers.
const isDispatchSessionCurrent = (config: SessionInternalConfig): boolean =>
  config._sessionDispatchAuthGeneration === getAuthGeneration() && config._sessionDispatchSubject === getSessionSubject();

const isSemanticRead = (config: SessionInternalConfig): boolean => {
  if (config.semanticWrite) return false;
  if (config._sessionSemanticRead) return true;
  const method = (config.method ?? 'get').toLowerCase();
  return method === 'get' || method === 'head';
};

const combineSignals = (signals: Array<AbortSignal | null | undefined>): AbortSignal | undefined => {
  const active = signals.filter((signal): signal is AbortSignal => Boolean(signal));
  if (active.length === 0) return undefined;
  if (active.length === 1) return active[0];
  return AbortSignal.any(active);
};

const callerCancellation = (config: SessionInternalConfig) => new axios.CanceledError('canceled', undefined, config);

const isImplicitScopeCurrent = (config: SessionInternalConfig): boolean => {
  const signal = config._sessionOriginOrganizationSignal;
  if (!signal) return true;
  if (signal.aborted || config._sessionOriginOrganizationGeneration !== getOrganizationGeneration()) return false;
  return !config._sessionOriginOrganizationWasCommitted || config._sessionOriginOrganization === getCommittedOrganization();
};

const isRequestCurrent = (config: SessionInternalConfig, transportEntered: boolean): boolean => {
  if ((config.authPolicy ?? 'session') === 'session' && !isDispatchSessionCurrent(config)) return false;
  if (config.operationRequirement) {
    return isOperationRequirementCurrent(config.operationRequirement, {
      transportEntered,
      semanticRead: isSemanticRead(config),
    });
  }
  return isImplicitScopeCurrent(config);
};

// ETags are a cache publication surface. A workflow-start response may reach
// an actor-current continuation after an organization switch, but it must not
// seed the old tenant's cache once route scope is stale.
const isCachePublicationCurrent = (config: SessionInternalConfig): boolean => {
  if ((config.authPolicy ?? 'session') === 'session' && !isDispatchSessionCurrent(config)) return false;
  return config.operationRequirement ? config.operationRequirement.context.isScopeCurrent() : isImplicitScopeCurrent(config);
};

const rethrowInterceptorError = (error: unknown): never => {
  throw error;
};

export const clearSessionEtags = (): void => {
  for (const store of etagStores) store.clear();
};

export const __resetFailedRefreshStateForTests = (): void => {
  failedRefreshCount = 0;
  lastFailedRefreshTime = 0;
};

const applySessionRequest = (
  requestConfig: InternalAxiosRequestConfig,
  etagMethods: ReadonlySet<string>,
  etagStore: Map<string, string>,
  transport?: 'api' | 'channelApi'
) => {
  const config = requestConfig as SessionInternalConfig;
  config._sessionTransport ??= transport;
  const headers = AxiosHeaders.from(config.headers);
  config.headers = headers;

  if (config._sessionDispatchAuthGeneration === undefined) {
    config._sessionDispatchAuthGeneration = getAuthGeneration();
    config._sessionDispatchSubject = getSessionSubject();
    config._sessionDispatchAuthSignal = getSessionAuthSignal();
    config._sessionCallerSignal = config.signal as AbortSignal | undefined;
    if (config._sessionCallerSignal && !isSemanticRead(config)) {
      emitSessionTelemetry({ code: 'write_signal_rejected', transport: config._sessionTransport, path: config.url });
      throw new TypeError('Durable writes do not accept caller AbortSignal values');
    }
  } else if ((config.authPolicy ?? 'session') === 'session' && !isDispatchSessionCurrent(config)) {
    throw new SessionTransitionError();
  }

  if (config.params?.query && typeof config.params.query === 'object') {
    config.params = {
      ...config.params,
      query: JSON.stringify(config.params.query),
    };
  }

  // Session token injection reads the live token at request time; nothing is
  // stored on axios defaults, so no shared mutable header state exists.
  if ((config.authPolicy ?? 'session') === 'anonymous') {
    headers.delete(AUTHORIZATION_HEADER);
  } else if (!headers.has(AUTHORIZATION_HEADER)) {
    const token = getAccessToken();
    if (token) {
      headers.set(AUTHORIZATION_HEADER, `Bearer ${token}`);
      config._sessionAuthInjected = true;
    }
  }

  let scope: TenantScope | undefined;
  if (config.organizationScope !== undefined) {
    try {
      scope = validateTenantScope(config.organizationScope);
      config.organizationScope = scope;
    } catch {
      emitSessionTelemetry({ code: 'organization_scope_invalid', transport: config._sessionTransport, path: config.url });
      throw new SessionTransitionError();
    }
  }
  if (scope) {
    if (scope.kind === 'organization') {
      headers.set(ORGANIZATION_HEADER, scope.organization);
    } else {
      headers.delete(ORGANIZATION_HEADER);
    }
  } else {
    emitSessionTelemetry({ code: 'organization_scope_missing', transport: config._sessionTransport, path: config.url });
    throw new SessionTransitionError();
  }

  const organizationSent = headers.get(ORGANIZATION_HEADER);
  const organizationString = organizationSent == null ? undefined : String(organizationSent);
  const shouldCaptureOrganization =
    Boolean(organizationString) &&
    (scope?.kind === 'organization' || (!scope && (!hasCommittedOrganizationIdentity() || organizationString === getCommittedOrganization())));
  if (!config._sessionOriginOrganizationSignal && organizationString && shouldCaptureOrganization) {
    config._sessionOriginOrganization = organizationString;
    config._sessionOriginOrganizationGeneration = getOrganizationGeneration();
    config._sessionOriginOrganizationSignal = getOrganizationSignal();
    config._sessionOriginOrganizationWasCommitted = hasCommittedOrganizationIdentity();
  }

  // Cross-target admin commands carry the module-private capability (the
  // header names the administered target while the requirement keeps actor
  // route liveness). Every other explicit scope must agree with
  // the operation requirement's organization when one is present, or with the
  // committed organization when none is - a caller that pairs
  // requestScopeFor(orgA) with an orgB operation fails closed here instead of
  // silently sending orgA's header.
  const isAdminTarget = isAdminTargetCapability(config._sessionAdminTargetCapability);
  if (config._sessionAdminTargetCapability !== undefined && !isAdminTarget) {
    emitSessionTelemetry({ code: 'organization_scope_mismatch', transport: config._sessionTransport, path: config.url });
    throw new SessionTransitionError();
  }
  if (scope?.kind === 'organization' && !isAdminTarget) {
    const requirementOrganization = config.operationRequirement?.context.organization;
    if (requirementOrganization !== undefined && organizationString !== requirementOrganization) {
      emitSessionTelemetry({ code: 'organization_scope_mismatch', transport: config._sessionTransport, path: config.url });
      throw new SessionTransitionError();
    }
    if (!config.operationRequirement && config._sessionOriginOrganizationWasCommitted && organizationString !== getCommittedOrganization()) {
      emitSessionTelemetry({ code: 'organization_scope_mismatch', transport: config._sessionTransport, path: config.url });
      throw new SessionTransitionError();
    }
  }
  if (isAdminTarget && !config.operationRequirement) throw new SessionTransitionError();
  if (!config.operationRequirement && scope?.kind === 'organization' && !isImplicitScopeCurrent(config)) {
    throw new SessionTransitionError();
  }

  if (config.operationRequirement) {
    const current = isOperationRequirementCurrent(config.operationRequirement, {
      transportEntered: Boolean(config._sessionOperationDispatched),
      semanticRead: isSemanticRead(config),
    });
    if (!current) throw new SessionTransitionError();
  }

  if (isSemanticRead(config)) {
    const lifecycleSignal = config.operationRequirement
      ? operationRequirementSignal(config.operationRequirement)
      : combineSignals([
          config._sessionOriginOrganizationSignal,
          (config.authPolicy ?? 'session') === 'session' ? config._sessionDispatchAuthSignal : undefined,
        ]);
    config._sessionReadSignal = lifecycleSignal;
    config.signal = combineSignals([config._sessionCallerSignal, lifecycleSignal]);
  } else {
    // Never bind an organization/session liveness signal to a write once it
    // can be on the wire (P2). Caller cancellation remains caller-owned.
    config._sessionReadSignal = undefined;
    config.signal = config._sessionCallerSignal;
  }

  // Scope the legacy URL-keyed ETag identity by organization so a record
  // fetched under one tenant can never arm If-Match for a save under
  // another. Path identity is preserved (no getUri normalization) so
  // previously distinct relative/absolute call sites stay distinct.
  config._sessionEtagKey = `${organizationSent == null ? '__no-organization__' : String(organizationSent)}|${config.url ?? ''}`;

  if (config.method && etagMethods.has(config.method.toLowerCase()) && config._sessionEtagKey) {
    const etag = etagStore.get(config._sessionEtagKey);
    if (etag) headers.set('If-Match', `"${etag}"`);
  }

  // Axios reuses the config for a 401 replay. Wrap the adapter once so a
  // `start` write remains scope-current through every request interceptor and
  // downgrades to actor-current only at the closest observable transport
  // boundary. A replay sees the durable-entry flag and remains actor-current.
  if (config.operationRequirement?.phase === 'start' && !isSemanticRead(config) && !config._sessionOperationAdapterWrapped) {
    const adapter = axios.getAdapter(config.adapter) as AxiosAdapter;
    config._sessionOperationAdapterWrapped = true;
    config.adapter = (adapterConfig) => {
      const sessionConfig = adapterConfig as SessionInternalConfig;
      const requirement = sessionConfig.operationRequirement;
      if (
        requirement &&
        !isOperationRequirementCurrent(requirement, {
          transportEntered: Boolean(sessionConfig._sessionOperationDispatched),
          semanticRead: false,
        })
      ) {
        throw new SessionTransitionError();
      }
      if (!requirement) throw new SessionTransitionError();
      sessionConfig._sessionOperationDispatched = true;
      markOperationRequirementTransportEntered(requirement);
      return adapter(adapterConfig);
    };
  }
  return config;
};

export const configureSessionInterceptors = (
  instance: AxiosInstance,
  {
    etagMethods,
    transport,
  }: {
    etagMethods: ReadonlySet<string>;
    transport?: 'api' | 'channelApi';
  }
): void => {
  const etagStore = new Map<string, string>();
  etagStores.add(etagStore);

  instance.interceptors.request.use(
    (requestConfig) => applySessionRequest(requestConfig, etagMethods, etagStore, transport),
    rethrowInterceptorError,
    {
      synchronous: true,
    }
  );

  instance.interceptors.response.use(
    (response) => {
      const config = response.config as SessionInternalConfig;
      if (!isRequestCurrent(config, true)) {
        emitSessionTelemetry({ code: 'stale_publication_suppressed', transport: config._sessionTransport, path: config.url });
        throw new SessionTransitionError();
      }
      const etag = response.headers.etag;
      if (etag && config._sessionEtagKey) {
        if (isCachePublicationCurrent(config)) {
          etagStore.set(config._sessionEtagKey, String(etag).replace(/W\/|"/g, ''));
        } else {
          emitSessionTelemetry({ code: 'stale_publication_suppressed', transport: config._sessionTransport, path: config.url });
        }
      }
      return response;
    },
    async (caught: unknown) => {
      const error = caught as AxiosError;
      const config = error.config as SessionInternalConfig | undefined;

      if (config?._sessionCallerSignal?.aborted) return Promise.reject(callerCancellation(config));

      if (config && !isRequestCurrent(config, true)) {
        emitSessionTelemetry({ code: 'stale_publication_suppressed', transport: config._sessionTransport, path: config.url });
        return Promise.reject(new SessionTransitionError());
      }

      if (axios.isCancel(caught) || (caught as { code?: string } | null)?.code === 'ERR_CANCELED') {
        if (config?._sessionReadSignal?.aborted) return Promise.reject(new SessionTransitionError());
        return Promise.reject(error);
      }

      if (!config || !isAuthenticationError(error) || config._sessionRetry || !config._sessionAuthInjected) {
        return Promise.reject(error);
      }

      // A session transition is not a refresh failure: reject quietly both
      // before spending a refresh on a dead session and after the await, in
      // case the transition happened during it.
      if (!isRequestCurrent(config, true)) {
        emitSessionTelemetry({ code: 'session_transition_replay_rejected', transport: config._sessionTransport, path: config.url });
        return Promise.reject(new SessionTransitionError());
      }

      config._sessionRetry = true;
      const refreshed = await refreshAccessToken();
      if (!isRequestCurrent(config, true)) {
        emitSessionTelemetry({ code: 'session_transition_replay_rejected', transport: config._sessionTransport, path: config.url });
        return Promise.reject(new SessionTransitionError());
      }
      if (!refreshed) {
        recordRefreshFailure();
        void requestReauthentication().catch(() => {
          // The original authenticated request still rejects through its
          // typed endpoint envelope; a failed redirect can be retried by the
          // next terminal authentication failure.
        });
        return Promise.reject(error);
      }

      const token = getAccessToken();
      if (!token) {
        recordRefreshFailure();
        void requestReauthentication().catch(() => {});
        return Promise.reject(error);
      }

      failedRefreshCount = 0;
      config.headers = AxiosHeaders.from(config.headers);
      config.headers.set(AUTHORIZATION_HEADER, `Bearer ${token}`);
      // The replay keeps its original organization header (P3 continuation):
      // the request interceptor re-runs but never overwrites a present
      // header, and actor currency was just re-verified above.
      return instance(config);
    }
  );
};
